import { get, patch, post } from './api'

export interface UnitComponent {
  component_id: string; slug: string; name: string; status: string; part: string
  need: 'required' | 'wanted'; note: string | null
}
export interface Unit {
  id: string; slug: string; name: string; kind: string; tagline: string | null
  intent: string | null; lifecycle: string; priority: number | null
  repo_path: string | null; live_url: string | null; source_path: string | null; notes: string | null
  components: UnitComponent[]; blockers: string[]
}
export interface Component {
  id: string; slug: string; name: string; part: string; status: string
  summary: string; origin: string | null; location: string | null
}
export interface Part { part: string; components: Component[]; built: number; total: number; readiness: number }
export interface Takeaway {
  id: string; rule: string; why: string | null; category: string; severity: string
  unit_name: string | null; component_name: string | null
}
export interface LogEntry {
  id: string; happened_on: string; kind: string; summary: string
  detail: string | null; evidence: string | null; state: string
  unit_name: string | null; component_name: string | null
}

export const registry = {
  units: () => get<{ units: Unit[]; lifecycles: string[] }>('/api/registry/units'),
  components: () => get<{ parts: Part[]; total: number; built: number }>('/api/registry/components'),
  takeaways: (category?: string) =>
    get<{ takeaways: Takeaway[] }>(`/api/registry/takeaways${category ? `?category=${encodeURIComponent(category)}` : ''}`),
  createUnit: (body: { slug: string; name: string; kind?: string; tagline?: string; intent?: string; lifecycle?: string }) =>
    post<{ slug: string }>('/api/registry/units', body),
  updateUnit: (slug: string, body: { lifecycle?: string; priority?: number | null; notes?: string }) =>
    patch<{ ok: true }>(`/api/registry/units/${slug}`, body),
  addTakeaway: (body: { rule: string; why?: string; category?: string; severity?: string }) =>
    post<{ ok: true }>('/api/registry/takeaways', body),
  log: (q?: { kind?: string; state?: string }) => {
    const p = new URLSearchParams()
    if (q?.kind) p.set('kind', q.kind)
    if (q?.state) p.set('state', q.state)
    const qs = p.toString()
    return get<{ log: LogEntry[]; open: number; kinds: string[] }>(`/api/registry/log${qs ? `?${qs}` : ''}`)
  },
  addLogEntry: (body: { summary: string; detail?: string; evidence?: string; kind?: string; state?: string }) =>
    post<{ ok: true }>('/api/registry/log', body),
}

/**
 * Mirrors `services/api/src/registry.ts` KINDS. The server rejects anything
 * else, so a value added on one side and not the other fails at the boundary
 * rather than writing a unit that renders unstyled.
 */
export const UNIT_KINDS = ['app', 'site', 'nano-app', 'service', 'course', 'product'] as const

/** Human labels for the body parts, and what each limb is FOR. */
export const PART_META: Record<string, { label: string; role: string }> = {
  head:        { label: 'Head',      role: 'Identity — who someone is, and how they prove it' },
  torso:       { label: 'Torso',     role: 'The spine every unit has whether it wants one or not' },
  'right-arm': { label: 'Right arm', role: 'Money in' },
  'left-arm':  { label: 'Left arm',  role: 'Reach — getting hold of a person' },
  'right-leg': { label: 'Right leg', role: 'Content — being found, and being readable' },
  'left-leg':  { label: 'Left leg',  role: 'Place and device — where and on what' },
}
